// @tennis-matches/services/governance
// Mapping zwischen UX-Aktivitäten ("Was möchtest du tun?") und RBAC-Rollen/Apps.
//
// Das Mapping ist die einzige Stelle, an der UX-Aktivitäten an die bestehende
// Governance-Welt gebunden werden. RBAC selbst (role_bindings, access_requests,
// role_app_access) bleibt unverändert. UI-Schichten dürfen ausschließlich über
// dieses Modul auf die Rollenlisten zugreifen.
//
// Aktivitäten sind absichtlich grobgranular gehalten. Wenn eine Aktivität mehrere
// RBAC-Rollen umfasst (z. B. "trainer" → trainer + optional trainer_coordinator),
// liegt die Feinwahl im jeweiligen Onboarding-Sheet.

/** @typedef {"connect_club"|"family"|"training"} HubActivityId */

/**
 * @typedef {Object} HubActivityDefinition
 * @property {HubActivityId} id
 * @property {string} titleKey
 * @property {string} descriptionKey
 * @property {string} iconKey                 - Symbol-Hinweis für Icon-Mapping in der UI.
 * @property {readonly string[]} roleOptions  - Mögliche RBAC-Feinrollen (kann leer sein
 *                                              für reine Onboarding-Schritte wie connect_club).
 * @property {readonly string[]} unlocksApps  - App-Keys, die durch eine erfolgreiche
 *                                              Freigabe sichtbar werden (informativ).
 * @property {boolean} requiresClubScope      - Aktiviert die Vereinsbindung als Voraussetzung.
 * @property {boolean} allowsIndependent      - Aktivität kann ohne Verein eingereicht werden
 *                                              (z. B. unabhängiger Trainer).
 */

const __freeze = (obj) => Object.freeze(obj);

/** @type {HubActivityDefinition} */
const ACTIVITY_CONNECT_CLUB = __freeze({
  id: "connect_club",
  titleKey: "hub.onboarding.activity.connectClub.title",
  descriptionKey: "hub.onboarding.activity.connectClub.description",
  iconKey: "club",
  roleOptions: __freeze([]),
  unlocksApps: __freeze([]),
  requiresClubScope: false,
  allowsIndependent: false,
});

/** @type {HubActivityDefinition} */
const ACTIVITY_FAMILY = __freeze({
  id: "family",
  titleKey: "hub.onboarding.activity.family.title",
  descriptionKey: "hub.onboarding.activity.family.description",
  iconKey: "family",
  roleOptions: __freeze(["parent"]),
  unlocksApps: __freeze(["trainerstunden"]),
  requiresClubScope: true,
  allowsIndependent: false,
});

/** @type {HubActivityDefinition} */
const ACTIVITY_TRAINING = __freeze({
  id: "training",
  titleKey: "hub.onboarding.activity.training.title",
  descriptionKey: "hub.onboarding.activity.training.description",
  iconKey: "training",
  roleOptions: __freeze(["player"]),
  unlocksApps: __freeze(["trainerstunden"]),
  requiresClubScope: true,
  allowsIndependent: false,
});

/**
 * Trainer-Onboarding im Hub-Hero ist deaktiviert (Self-Service-Risiko: ungewollter
 * ClubHub-Zugriff). Trainer-Rollen nur noch über Admin/Invite-Flows vergeben.
 */

/** Vollständige, reihenfolge-sensitive Liste aller Hub-Aktivitäten. */
export const HUB_ACTIVITIES = __freeze([
  ACTIVITY_CONNECT_CLUB,
  ACTIVITY_FAMILY,
  ACTIVITY_TRAINING,
]);

/** Liefert die Aktivitäts-Definition zu einer ID — oder `null`. */
export function getHubActivityById(activityId) {
  const id = String(activityId || "").trim();
  if (!id) return null;
  return HUB_ACTIVITIES.find((a) => a.id === id) || null;
}

/** Welche RBAC-Rollen markieren eine Aktivität als "abgeschlossen"? */
export function getActivityCompletionRoles(activityId) {
  const def = getHubActivityById(activityId);
  if (!def) return [];
  return [...def.roleOptions];
}

/** Welche Apps werden durch die Aktivität freigeschaltet (rein informativ)? */
export function getActivityUnlockedAppKeys(activityId) {
  const def = getHubActivityById(activityId);
  if (!def) return [];
  return [...def.unlocksApps];
}
